import { useUnenrollmentRouteContext } from '../index'
import Text from '@/components/Text'
import { useAppSelector, useAppDispatch } from '@/hooks'
import { selectProgramEnrolledDevices } from '@/reducers/programsNew'
import { Button } from '@/components'
import { AlertMessage } from '@/app/components'
import { useNavigation } from '@/app/hooks'
import Checkbox from '@/components/forms/checkbox'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import {
  selectInProgressUnenrollment,
  setDeviceSelection,
} from '@/reducers/inProgressUtilityProgramUnenrollments'
import {
  unenrollmentChargerDisplayName,
  unenrollmentThermostatDisplayName,
  unenrollmentVehicleDisplayName,
} from '../utils'

const validationSchema = Yup.object({
  selectedVehicles: Yup.array().default([]),
  selectedThermostats: Yup.array().default([]),
  selectedChargers: Yup.array().default([]),
  selectedEnergySystems: Yup.array().default([]),
}).test(
  'at-least-one-device',
  'Please select at least one device to unenroll',
  function (value) {
    const totalSelected =
      (value.selectedVehicles?.length || 0) +
      (value.selectedThermostats?.length || 0) +
      (value.selectedChargers?.length || 0) +
      (value.selectedEnergySystems?.length || 0)
    return totalSelected > 0
  },
)
type FormValues = Yup.InferType<typeof validationSchema>

function useViewModel() {
  const { utilityProgram } = useUnenrollmentRouteContext()
  const navigate = useNavigation()
  const dispatch = useAppDispatch()
  const inProgressUnenrollment = useAppSelector(
    selectInProgressUnenrollment(utilityProgram.id),
  )
  const devices = useAppSelector(
    selectProgramEnrolledDevices(utilityProgram.id),
  )
  const defaultValues: FormValues = {
    selectedVehicles:
      inProgressUnenrollment?.deviceSelection.vehicleIds.map((v) =>
        v.toString(),
      ) ?? [],
    selectedThermostats:
      inProgressUnenrollment?.deviceSelection.thermostatIds.map((v) =>
        v.toString(),
      ) ?? [],
    selectedChargers:
      inProgressUnenrollment?.deviceSelection.chargerIds.map((v) =>
        v.toString(),
      ) ?? [],
    selectedEnergySystems:
      inProgressUnenrollment?.deviceSelection.energySystemIds.map((v) =>
        v.toString(),
      ) ?? [],
  }

  const onSubmit = (values: FormValues) => {
    dispatch(
      setDeviceSelection({
        utilityProgramId: utilityProgram.id,
        deviceSelection: {
          vehicleIds: values.selectedVehicles.map((id) => +id),
          thermostatIds: values.selectedThermostats.map((id) => +id),
          chargerIds: values.selectedChargers.map((id) => +id),
          energySystemIds: values.selectedEnergySystems.map((id) => String(id)),
        },
      }),
    )

    navigate.push(`/utility-program/${utilityProgram.id}/unenroll/reason`)
  }

  return { devices, defaultValues, validationSchema, onSubmit }
}

export default function UnenrollDeviceSelect() {
  const { devices, defaultValues, validationSchema, onSubmit } = useViewModel()

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = useForm<{
    selectedVehicles: string[]
    selectedThermostats: string[]
    selectedChargers: string[]
    selectedEnergySystems: string[]
  }>({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  })

  const handleCheckboxChange = (
    currentValues: string[],
    value: string,
    onChange: (value: string[]) => void,
  ) => {
    const isSelected = currentValues.includes(value)
    const newValues = isSelected
      ? currentValues.filter((id) => id !== value)
      : [...currentValues, value]
    onChange(newValues)
  }

  return (
    <form
      className="flex flex-col gap-8 h-full"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Text variant="h3">
        Which devices would you like to unenroll?
        <span className="text-red-500">*</span>
      </Text>

      {devices.vehicles.length > 0 && (
        <section className="flex flex-col">
          {devices.vehicles.map((v) => (
            <div key={v.id} className="flex flex-row gap-4 items-center">
              <Controller
                name="selectedVehicles"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id={`vehicle-${v.id}`}
                    name={field.name}
                    value={v.id.toString()}
                    checked={field.value.includes(v.id.toString())}
                    onChange={() => {
                      handleCheckboxChange(
                        field.value,
                        v.id.toString(),
                        field.onChange,
                      )
                    }}
                  />
                )}
              />
              <label htmlFor={`vehicle-${v.id}`}>
                {unenrollmentVehicleDisplayName(v)}
              </label>
            </div>
          ))}
        </section>
      )}

      {devices.thermostats.length > 0 && (
        <section className="flex flex-col gap-4">
          {devices.thermostats.map((t) => (
            <div key={t.id} className="flex flex-row gap-4 items-center">
              <Controller
                name="selectedThermostats"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id={`thermostat-${t.id}`}
                    name={field.name}
                    value={t.id.toString()}
                    checked={field.value.includes(t.id.toString())}
                    onChange={() => {
                      handleCheckboxChange(
                        field.value,
                        t.id.toString(),
                        field.onChange,
                      )
                    }}
                  />
                )}
              />
              <label htmlFor={`thermostat-${t.id}`}>
                {unenrollmentThermostatDisplayName(t)}
              </label>
            </div>
          ))}
        </section>
      )}

      {devices.chargers.length > 0 && (
        <section className="flex flex-col gap-4">
          {devices.chargers.map((c) => (
            <div key={c.id} className="flex flex-row gap-4 items-center">
              <Controller
                name="selectedChargers"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id={`charger-${c.id}`}
                    name={field.name}
                    value={c.id.toString()}
                    checked={field.value.includes(c.id.toString())}
                    onChange={() => {
                      handleCheckboxChange(
                        field.value,
                        c.id.toString(),
                        field.onChange,
                      )
                    }}
                  />
                )}
              />
              <label htmlFor={`charger-${c.id}`}>
                {unenrollmentChargerDisplayName(c)}
              </label>
            </div>
          ))}
        </section>
      )}

      <AlertMessage variant="warning">
        When you unenroll a device, you will lose any program rewards you were
        earning for it.
      </AlertMessage>

      <span className="flex-auto"></span>
      <Button
        id="unenroll-next-device-select"
        type="submit"
        disabled={isSubmitting || !isValid}
      >
        Next
      </Button>
    </form>
  )
}
